import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const HCCAdminDashboard = () => {
  const { user } = useAuth();
  const [overviewData, setOverviewData] = useState(null);
  const [criticalItems, setCriticalItems] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('90');
  const [reassignModal, setReassignModal] = useState({ show: false, item: null });

  useEffect(() => {
    loadOverviewData();
    loadCriticalItems();
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedAgent) {
      loadAgentDetails(selectedAgent);
    }
  }, [selectedAgent, selectedPeriod]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/hcc/overview?period=${selectedPeriod}`);
      setOverviewData(response.data.data);
    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCriticalItems = async () => {
    try {
      const response = await axios.get('/api/admin/hcc/critical-items');
      setCriticalItems(response.data.data);
    } catch (error) {
      console.error('Error loading critical items:', error);
    }
  };

  const loadAgentDetails = async (agentId) => {
    try {
      const response = await axios.get(`/api/admin/hcc/agent-details/${agentId}?period=${selectedPeriod}`);
      setAgentDetails(response.data.data);
    } catch (error) {
      console.error('Error loading agent details:', error);
    }
  };

  const reassignHCC = async (hccId, newAgentId, reason) => {
    try {
      await axios.put(`/api/admin/hcc/${hccId}/assign-agent`, {
        newAgentId,
        reason
      });
      loadOverviewData();
      loadCriticalItems();
      setReassignModal({ show: false, item: null });
    } catch (error) {
      console.error('Error reassigning HCC:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getAlertColor = (urgencyLevel) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[urgencyLevel] || colors.medium;
  };

  const getPerformanceBadge = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    if (score >= 40) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading && !overviewData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
        <span className="ml-2">Loading Admin HCC Dashboard...</span>
      </div>
    );
  }

  const enterpriseStats = overviewData?.enterpriseStats || {};
  const agentPerformance = overviewData?.agentPerformance || [];
  const industryKPIs = overviewData?.industryKPIs || {};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Enterprise HCC Management Dashboard
          </h1>
          <p className="text-gray-600">
            Complete oversight of all agent collections and performance
          </p>
        </div>

        {/* Period Control */}
        <div className="mb-6">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-input"
          >
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>
        </div>

        {/* Enterprise KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total at Risk</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(enterpriseStats.totalBrokerFeesAtRisk)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {enterpriseStats.totalHCCDeclines} declines
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Recovered</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(enterpriseStats.totalRecovered)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {industryKPIs.actualRecoveryRate}% rate
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Commission Loss</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(enterpriseStats.totalCommissionLoss)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Potential impact
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">High Value</p>
                <p className="text-2xl font-bold text-purple-600">
                  {enterpriseStats.highValueDeclines}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  >$1K declines
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Critical Overdue</p>
                <p className="text-2xl font-bold text-red-700">
                  {enterpriseStats.criticalOverdueCount}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  30+ days
                </p>
              </div>
              <div className="p-2 bg-red-200 rounded-lg">
                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Benchmark Comparison */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Performance vs Industry Benchmarks</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">
                <span className={industryKPIs.actualRecoveryRate >= industryKPIs.targetRecoveryRate ? 'text-green-600' : 'text-red-600'}>
                  {industryKPIs.actualRecoveryRate}%
                </span>
              </div>
              <div className="text-sm text-gray-500">Recovery Rate</div>
              <div className="text-xs text-gray-400">Target: {industryKPIs.targetRecoveryRate}%</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">
                <span className="text-blue-600">{industryKPIs.averageCollectionDays}</span>
              </div>
              <div className="text-sm text-gray-500">Avg Collection Days</div>
              <div className="text-xs text-gray-400">Target: {industryKPIs.targetCollectionTime} days</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">
                <span className="text-orange-600">{industryKPIs.monthlyDeclineRate}%</span>
              </div>
              <div className="text-sm text-gray-500">Monthly Decline Rate</div>
              <div className="text-xs text-gray-400">Industry avg: 3.5%</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Agent Performance
            </button>
            <button
              onClick={() => setActiveTab('critical')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'critical'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Critical Alerts ({criticalItems.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics & Trends
            </button>
          </nav>
        </div>

        {/* Agent Performance Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Agent Rankings */}
            <div className="xl:col-span-2 bg-white rounded-lg shadow-md">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Agent Performance Rankings</h3>
                <p className="text-gray-600 mt-1">Ranked by collection performance score</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recovery Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        At Risk
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recovered
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agentPerformance.map((agent, index) => (
                      <tr key={agent._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-yellow-600' : 'bg-gray-300'
                            }`}>
                              {index + 1}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="font-medium text-gray-900">{agent.agentName || agent._id}</div>
                            <div className="text-sm text-gray-500">{agent._id}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerformanceBadge(agent.performanceScore)}`}>
                            {Math.round(agent.performanceScore)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${Math.min(agent.recoveryRate, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium">{agent.recoveryRate.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-red-600">
                          {formatCurrency(agent.totalAtRisk)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-green-600">
                          {formatCurrency(agent.totalRecovered)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setSelectedAgent(agent._id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Agent Detail Panel */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Agent Details</h3>
              </div>
              <div className="p-6">
                {selectedAgent && agentDetails ? (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {agentDetails.agent.firstName} {agentDetails.agent.lastName}
                      </h4>
                      <p className="text-sm text-gray-500">{agentDetails.agent.agentId}</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Total Declines:</span>
                        <span className="font-medium">{agentDetails.metrics.totalDeclines}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Amount at Risk:</span>
                        <span className="font-medium text-red-600">
                          {formatCurrency(agentDetails.metrics.totalAtRisk)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Recovered:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(agentDetails.metrics.totalRecovered)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Recovery Rate:</span>
                        <span className="font-medium">
                          {agentDetails.teamComparison.agentRecoveryRate}%
                        </span>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h5 className="font-medium mb-2">vs Team Average</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Recovery Rate:</span>
                          <span className={agentDetails.teamComparison.agentRecoveryRate > agentDetails.teamComparison.teamAverageRecoveryRate ? 'text-green-600' : 'text-red-600'}>
                            {agentDetails.teamComparison.agentRecoveryRate > agentDetails.teamComparison.teamAverageRecoveryRate ? '+' : ''}
                            {(agentDetails.teamComparison.agentRecoveryRate - agentDetails.teamComparison.teamAverageRecoveryRate).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {agentDetails.recentActivity.length > 0 && (
                      <div className="pt-4 border-t">
                        <h5 className="font-medium mb-2">Recent Activity</h5>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {agentDetails.recentActivity.map((activity, index) => (
                            <div key={index} className="text-xs text-gray-600">
                              <span className="font-medium">{activity.transactionId}</span>
                              <span className="mx-1">-</span>
                              <span>{activity.collectionStatus?.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">Select an agent to view details</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Critical Alerts Tab */}
        {activeTab === 'critical' && (
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-red-600">Critical HCC Items Requiring Immediate Attention</h3>
              <p className="text-gray-600 mt-1">High-value, overdue, or disputed items</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alert Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Overdue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {criticalItems.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-2 ${getAlertColor(item.urgencyLevel)}`}></div>
                          <span className="text-sm font-medium capitalize">{item.urgencyLevel}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">{item.clientInfo?.name}</div>
                          <div className="text-sm text-gray-500">{item.clientInfo?.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">{item.agentName}</div>
                          <div className="text-sm text-gray-500">{item.agentId}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-red-600">
                          {formatCurrency(item.hccDetails?.brokerFee)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium">{item.daysSinceDecline} days</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {item.collectionStatus?.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => setReassignModal({ show: true, item })}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Decline Trends</h3>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
                <p className="text-gray-500">Decline Trend Chart (Chart.js integration needed)</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Collection Status Distribution</h3>
              <div className="space-y-3">
                {overviewData?.collectionBreakdown?.map((status) => (
                  <div key={status._id} className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                      <span className="capitalize font-medium">{status._id.replace('_', ' ')}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(status.totalAmount)}</div>
                      <div className="text-sm text-gray-500">({status.count} items)</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reassign Modal */}
        {reassignModal.show && (
          <ReassignModal
            item={reassignModal.item}
            agents={agentPerformance}
            onClose={() => setReassignModal({ show: false, item: null })}
            onReassign={reassignHCC}
          />
        )}
      </div>
    </div>
  );
};

// Reassign Modal Component
const ReassignModal = ({ item, agents, onClose, onReassign }) => {
  const [formData, setFormData] = useState({
    newAgentId: '',
    reason: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.newAgentId) return;
    onReassign(item._id, formData.newAgentId, formData.reason);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Reassign HCC Collection Item
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Agent: {item.agentName} ({item.agentId})
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reassign to Agent
              </label>
              <select
                value={formData.newAgentId}
                onChange={(e) => setFormData({...formData, newAgentId: e.target.value})}
                className="form-input w-full"
                required
              >
                <option value="">Select new agent...</option>
                {agents.filter(agent => agent._id !== item.agentId).map(agent => (
                  <option key={agent._id} value={agent._id}>
                    {agent.agentName || agent._id} - Score: {Math.round(agent.performanceScore)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Reassignment
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="form-input w-full h-20"
                placeholder="Explain why this item is being reassigned..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary px-4 py-2 text-sm"
              >
                Reassign HCC
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HCCAdminDashboard;